// MonthlyBreakdownPrint.js
import React, { useMemo } from 'react';

/**
 * IMPORTANT: If you later refactor helpers (normalizeDrugName, computeDrugCost, etc.)
 * move them into a shared file (e.g. monthlyBreakdownUtils.js) and import here
 * instead of duplicating. For now we inline a trimmed version to keep this file
 * self-contained and safe for static render.
 */

/* ---------- Helper Utilities (copied / trimmed from full component) ---------- */
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    CartesianGrid,
    ReferenceLine,
  } from 'recharts';

const monthAbbrevMap = {
  January: 'Jan',
  February: 'Feb',
  March: 'Mar',
  April: 'Apr',
  May: 'May',
  June: 'Jun',
  July: 'Jul',
  August: 'Aug',
  September: 'Sep',
  October: 'Oct',
  November: 'Nov',
  December: 'Dec',
};
function applyM3P({ rows, startMonth, annualCap = 2000 }) {
   // Clone so we don't mutate original
   const out = rows.map(r => ({ ...r }));

   // Sum what patient already paid before plan starts
   const paidBeforeStart = out.slice(0, startMonth).reduce((s, r) => s + (r.cost || 0), 0);

   let remainingBalanceDue = 0;          // amount still to spread
   let cumulativePaidSoFar = paidBeforeStart; // total OOP thru current month (pre‑plan months included)

   const totalMonths = out.length;

   return out.map((row, idx) => {
     if (idx < startMonth) {
       return {
         ...row,
         m3pMonthlyCap: null,
         m3pBill: null,
         balanceInM3P: null,
         m3pExplanation: 'Payment plan not started.'
       };
     }

     const monthsRemaining = totalMonths - idx;
     const thisMonthCost = row.cost || 0;

     let monthlyCap, bill;

     if (idx === startMonth) {
       // FIRST plan month: cap so we don't exceed 2k total for the year
       const amountToReachThreshold = Math.max(annualCap - cumulativePaidSoFar, 0);
       monthlyCap = monthsRemaining ? amountToReachThreshold / monthsRemaining : 0;
       bill = Math.min(monthlyCap, thisMonthCost + (remainingBalanceDue / monthsRemaining));
     } else {
       // SUBSEQUENT months: divide remaining balance (incl. this month) evenly
       const adjustedRemainingBalance = remainingBalanceDue + thisMonthCost;
       monthlyCap = monthsRemaining ? adjustedRemainingBalance / monthsRemaining : 0;
       bill = Math.min(monthlyCap, thisMonthCost + (remainingBalanceDue / monthsRemaining));
     }

     remainingBalanceDue = remainingBalanceDue + thisMonthCost - bill;
     cumulativePaidSoFar += thisMonthCost;

     return {
       ...row,
       m3pMonthlyCap: monthlyCap,
       m3pBill: bill,
       balanceInM3P: remainingBalanceDue,
       m3pExplanation: `monthsLeft=${monthsRemaining}; cap=${monthlyCap.toFixed(2)}; bill=${bill.toFixed(2)}; bal=${remainingBalanceDue.toFixed(2)}`
     };
   });
 }

// --------------------------------------------
/**
   * PaymentPlanChart
   * @param {Object[]} data - e.g. [ { month: "January", outOfPocket: 400, m3pBill: 200 }, ... ]
   * @param {number} paymentPlanStartMonth - 0-based index (0=Jan, 1=Feb, etc.) for ReferenceLine
   */
  function PaymentPlanChart({
  data,
  paymentPlanStartMonth,
  showM3P,
  chartWidth = 750,
  chartHeight = 350,
}) {
  // find first month with non‐zero outOfPocket
  let firstNonZero = -1;
  data.forEach((r, i) => {
    if (firstNonZero === -1 && r.outOfPocket > 0) firstNonZero = i;
  });

  // build chart data
  const chartData = data.map((r, i) => {
    const monthAbbrev = monthAbbrevMap[r.month] || r.month;
    const outOfPocket =
      firstNonZero === -1 || i < firstNonZero ? null : r.outOfPocket || 0;
    const m3pBill =
      showM3P && r.m3pBill !== undefined
        ? firstNonZero === -1 || i < firstNonZero
          ? null
          : r.m3pBill
        : undefined;
    return { monthAbbrev, outOfPocket, ...(m3pBill != null ? { m3pBill } : {}) };
  });

  // compute ref‐line x
  let refLineX = null;
  if (
    showM3P &&
    paymentPlanStartMonth >= 0 &&
    paymentPlanStartMonth < chartData.length
  ) {
    refLineX = chartData[paymentPlanStartMonth].monthAbbrev;
  }

  const tooltipFormatter = (val, name) =>
    typeof val === "number" ? [`$${val.toFixed(2)}`, name] : val;

  // prepare HTML legend items
  const legendItems = [
    { label: "Out‑of‑Pocket (w/o M3P)", color: "#2f4b7c" },
    ...(showM3P ? [{ label: "M3P Bill", color: "#ffa600" }] : []),
  ];

  return (
    <div style={{ marginTop: 24, textAlign: "center" }}>
      <LineChart
        width={chartWidth}
        height={chartHeight}
        data={chartData}
        margin={{ top: 12, right: 24, left: 16, bottom: 50 }} // lots of bottom space
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#ccc" strokeOpacity={0.7} />

        <XAxis
          dataKey="monthAbbrev"
          label={{ value: "Month", position: "insideBottom", offset: -15, fontSize: 14 }}
          tick={{ fontSize: 12 }}
        />

        <YAxis
          label={{
            value: "Cost ($)",
            angle: -90,
            position: "insideLeft",
            offset: 0,
            fontSize: 14,
          }}
          tick={{ fontSize: 12 }}
        />

        <RechartsTooltip formatter={tooltipFormatter} contentStyle={{ fontSize: 12 }} />

        {/* Out‑of‑Pocket line */}
        <Line
          type="linear"
          dataKey="outOfPocket"
          stroke="#2f4b7c"
          strokeWidth={3}
          dot={{ r: 5 }}
          activeDot={{ r: 7 }}
          name="Out‑of‑Pocket (w/o M3P)"
          connectNulls={false}
        />

        {/* M3P Bill line */}
        {showM3P && chartData.some((r) => r.m3pBill != null) && (
          <Line
            type="linear"
            dataKey="m3pBill"
            stroke="#ffa600"
            strokeWidth={3}
            dot={{ r: 5 }}
            activeDot={{ r: 7 }}
            name="M3P Bill"
            connectNulls={false}
          />
        )}

        {/* Reference line */}
        {showM3P && refLineX && (
          <ReferenceLine
            x={refLineX}
            stroke="red"
            strokeDasharray="5 5"
            label={{
              value: "Payment Plan Start",
              position: "insideTop",
              fill: "red",
              fontSize: 12,
              fontWeight: "bold",
            }}
          />
        )}
      </LineChart>

      {/* Manual HTML legend below the SVG */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 40,
          marginTop: 16,      // pulls up into the chart’s bottom margin
          paddingTop: 8,
          borderTop: "1px solid #d9dfe5",
          fontSize: 12,
          color: "#333",
        }}
      >
        {legendItems.map((item) => (
          <div key={item.label} style={{ display: "flex", alignItems: "center" }}>
            <span
              style={{
                display: "inline-block",
                width: 20,
                height: 2,
                backgroundColor: item.color,
                marginRight: 6,
              }}
            />
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}



function normalizeDrugName(name) {
  if (!name) return "";
  let normalized = name.toLowerCase();
  normalized = normalized.replace(/\d+/g, "");
  normalized = normalized.replace(/\b(mg|ml|mcg|g)\b/g, "");
  normalized = normalized.replace(/\b(tablet|oral|capsule|injection|suspension|solution|strip|kit)\b/g, "");
  // eslint-disable-next-line
  normalized = normalized.replace(/[\/\-\[\]\(\),]/g, " ");
  normalized = normalized.replace(/\s+/g, " ").trim();
  return normalized;
}

const COVERAGE_LABEL = {
  0: '0. Deductible',
  1: '1. Initial Coverage',
  3: '2. Catastrophic'
};

function formatCost(costType, amount, retailCost = null, deductible = 0, coverageLevel1 = null) {
  if (amount == null || amount === 'N/A') {
    if (costType === 3) {
      return { costDisplay: '100%', explanation: 'No retail cost; assume 100% until deductible met.', isDeductibleStage: true };
    }
    return { costDisplay: 'N/A', explanation: '', isDeductibleStage: false };
  }
  if (costType === 3) {
    if (typeof retailCost !== 'number') {
      return { costDisplay: '100%', explanation: 'No retail cost; assume full patient pay.', isDeductibleStage: true };
    }
    if (retailCost <= deductible) {
      return {
        costDisplay: `$${retailCost.toFixed(2)}`,
        explanation: `Retail ($${retailCost.toFixed(2)}) ≤ deductible ($${deductible.toFixed(2)}).`,
        isDeductibleStage: true
      };
    }
    const diff = retailCost - deductible;
    if (!coverageLevel1) {
      return { costDisplay: `$${retailCost.toFixed(2)}`, explanation: 'No level 1 coverage data.', isDeductibleStage: true };
    }
    const cov1Type = parseInt(coverageLevel1.cost_type_pref, 10);
    const cov1Amt = parseFloat(coverageLevel1.cost_amt_pref);
    if (cov1Type === 2) {
      const finalCost = deductible + diff * cov1Amt;
      return {
        costDisplay: `$${finalCost.toFixed(2)}`,
        explanation: `Deductible $${deductible.toFixed(2)} + ${(cov1Amt*100).toFixed(0)}% of $${diff.toFixed(2)}.`,
        isDeductibleStage: true
      };
    } else if (cov1Type === 1) {
      const finalCost = deductible + cov1Amt;
      return {
        costDisplay: `$${finalCost.toFixed(2)}`,
        explanation: `Deductible $${deductible.toFixed(2)} + copay $${cov1Amt.toFixed(2)}.`,
        isDeductibleStage: true
      };
    }
    return { costDisplay: `$${retailCost.toFixed(2)}`, explanation: 'Unhandled type; full cost.', isDeductibleStage: true };
  }
  if (costType === 0 || costType === 1) {
    return { costDisplay: `$${parseFloat(amount).toFixed(2)}`, explanation: '', isDeductibleStage: false };
  }
  if (costType === 2) {
    const pct = parseFloat(amount);
    let display = `${(pct*100).toFixed(0)}%`;
    let explanation = '';
    if (retailCost) {
      const val = (retailCost * pct).toFixed(2);
      display += ` ($${val})`;
      explanation = `Pays ${(pct*100).toFixed(0)}% of $${retailCost.toFixed(2)}.`;
    }
    return { costDisplay: display, explanation, isDeductibleStage: false };
  }
  return { costDisplay: String(amount), explanation: '', isDeductibleStage: false };
}

function processInsulin(record, frequency, selectedPharmacyType, startDate) {
  // record.out_of_pocket_cost insulin rows have undefined coverage_level
  // Simplified: treat level 0 & 1 same copay if present; catastrophic 0.
  const daysSupplyValue = (frequency === '30days' || frequency === '30') ? 1 : 2;
  const rows = record.out_of_pocket_cost || [];
  // Build coverage scaffolding
  const info = [
    { coverage_level:0 }, { coverage_level:1 }, { coverage_level:3 }
  ];
  const source = rows.find(r => r.days_supply === daysSupplyValue);
  let retail = null;
  if (source) {
    // Use any of its copays as the insulin capped cost
    const raw = source.copay_amt_pref_insln || source.copay_amt_nonpref_insln || source.copay_amt_mail_pref_insln || source.copay_amt_mail_nonpref_insln;
    if (raw != null) retail = parseFloat(raw);
    info.forEach(i => { i.amount = retail; });
  }
  const costBreakdown = info.map(i => ({
    coverage_level: i.coverage_level,
    stage: COVERAGE_LABEL[i.coverage_level],
    costNumber: retail,
    costDisplay: retail != null ? `$${retail.toFixed(2)}` : 'N/A',
    percentageDisplay: null,
    explanation: `Insulin capped cost at ${COVERAGE_LABEL[i.coverage_level]}`
  }));
  return {
    drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
    frequency,
    prescribedDate: startDate,
    retailCost: retail,
    costBreakdown,
    tier_level_value: record.tier_level_value
  };
}

function computeDrugCostPrint(record, {
  selectedDaysSupply = '30days',
  selectedPharmacyType = 'retail',
  deductible = 0,
  drugCosts = [],
  startDate = new Date(),
  frequency = '30days'
}) {
  if (!record) return null;

  const effectiveFrequency = frequency || record.frequency;
  const daysSupplyValue = (effectiveFrequency === '30days' || effectiveFrequency === '30') ? 1 : 2;

  // Not covered shortcut
  if (record.covered === false) {
    return {
      drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
      frequency: effectiveFrequency,
      prescribedDate: startDate,
      retailCost: null,
      costBreakdown: [],
      notCovered: true,
      tier_level_value: record.tier_level_value
    };
  }

  const costsArr = Array.isArray(record.out_of_pocket_cost) ? [...record.out_of_pocket_cost] : [];
  const isInsulin = costsArr.some(c => c.coverage_level === undefined);

  if (isInsulin) {
    return processInsulin(record, effectiveFrequency, selectedPharmacyType, startDate);
  }

  // Add synthetic deductible stage if plan has deductible but no level 0 row
  if (deductible > 0 && !costsArr.some(c => c.coverage_level === 0 && c.days_supply === daysSupplyValue)) {
    costsArr.unshift({
      coverage_level: 0,
      days_supply: daysSupplyValue,
      cost_type_pref: 3,
      cost_amt_pref: '1.00'
    });
  }

  // Build retail cost map
  const retailMap = {};
  drugCosts.forEach(dc => {
    const k = normalizeDrugName(dc.drug_name);
    if (!retailMap[k]) retailMap[k] = { retail: null, mail: null };
    if (dc.pharmacy_name?.toLowerCase().includes('mail')) {
      retailMap[k].mail = parseFloat(dc.retail_cost || 0);
    } else {
      retailMap[k].retail = parseFloat(dc.retail_cost || 0);
    }
  });

  let nameKey = '';
  if (record.nonproprietaryname) {
    nameKey = record.nonproprietaryname.replace(/\(generic\)/i,'').toLowerCase().trim();
  } else if (record.proprietaryname) {
    nameKey = record.proprietaryname.split(' ')[0].toLowerCase();
  }
  const normKey = normalizeDrugName(nameKey);

  let retailPair = retailMap[normKey];
  if (!retailPair) {
    const found = Object.entries(retailMap).find(([k]) => k.includes(normKey));
    retailPair = found ? found[1] : { retail: 0, mail: 0 };
  }

  let baseRetail;
  if (selectedPharmacyType === 'mail') {
    baseRetail = (effectiveFrequency === '30days' || effectiveFrequency === '30')
      ? (retailPair.mail ? retailPair.mail / 3 : null)
      : (retailPair.mail || null);
  } else {
    baseRetail = (effectiveFrequency === '30days' || effectiveFrequency === '30')
      ? (retailPair.retail || null)
      : (retailPair.retail ? retailPair.retail * 3 : null);
  }

  const coverageLevel1Entry = costsArr.find(c => c.coverage_level === 1 && c.days_supply === daysSupplyValue);

  const filtered = costsArr
    .filter(c => c.days_supply === daysSupplyValue && c.coverage_level !== 2)
    .sort((a,b) => a.coverage_level - b.coverage_level);

  const costBreakdown = filtered.map(row => {
    const pharmacyKey = row.coverage_level === 0
      ? 'cost_amt_pref'
      : (selectedPharmacyType === 'retail' ? 'cost_amt_pref' : 'cost_amt_mail_pref');
    const { costDisplay, explanation } = formatCost(
      row.cost_type_pref,
      row[pharmacyKey],
      typeof baseRetail === 'number' ? baseRetail : null,
      deductible,
      coverageLevel1Entry
    );

    let costNumber = null;
    let percentageDisplay = null;
    if (costDisplay.startsWith('$')) {
      costNumber = parseFloat(costDisplay.slice(1));
    } else if (costDisplay.includes('%')) {
      const m = costDisplay.match(/^(\d+)%\s*\(\$([\d.]+)\)/);
      if (m) {
        percentageDisplay = m[1] + '%';
        costNumber = parseFloat(m[2]);
      }
    }
    return {
      coverage_level: row.coverage_level,
      stage: COVERAGE_LABEL[row.coverage_level] || `Level ${row.coverage_level}`,
      costNumber,
      costDisplay,
      percentageDisplay,
      explanation
    };
  });

  return {
    drugName: record.nonproprietaryname || record.proprietaryname || 'Unknown',
    frequency: effectiveFrequency,
    prescribedDate: startDate,
    retailCost: baseRetail,
    costBreakdown,
    tier_level_value: record.tier_level_value
  };
}

/**
 * Simulate 12-month patient spend path (simplified to single drug context).
 * Returns { summaryRows, capMessage }
 */
function simulateYearSingleDrug({ breakdown, deductible, startDate }) {
  const catastrophicLimit = 2000;
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const startMonth = startDate.getMonth();
  let remainingDeductible = deductible;
  let trueOOP = 0;

  // For a single drug we assume refill monthly based on frequency:
  const freqDays = parseInt(breakdown.frequency, 10) || 30;
  const retail = breakdown.retailCost;
  const deductCost = breakdown.costBreakdown.find(c=>c.coverage_level===0)?.costNumber ?? breakdown.costBreakdown.find(c=>c.coverage_level===0)?.costDisplay;
  const initialCostNum = breakdown.costBreakdown.find(c=>c.coverage_level===1)?.costNumber ?? null;
  const initialPerc = breakdown.costBreakdown.find(c=>c.coverage_level===1)?.percentageDisplay ?? null;

  // Build event months: naive – one refill each frequency period
  const refillMonths = [];
  {
    let d = new Date(startDate);
    d.setHours(0,0,0,0);
    const end = new Date(startDate.getFullYear(), 11, 31);
    while (d <= end) {
      refillMonths.push(d.getMonth());
      d = new Date(d.getTime() + freqDays * 86400000);
    }
  }
  const refillMonthSet = new Set(refillMonths);

  const monthTotals = Array(12).fill(0);
  const monthTrueOOPAdd = Array(12).fill(0);
  const explanations = Array(12).fill(null).map(()=>[]);

  refillMonths.forEach(m => {
    let eventCost = 0;
    let exp = '';
    if (remainingDeductible > 0) {
      if (retail != null && retail <= remainingDeductible) {
        eventCost = typeof deductCost === 'number' ? deductCost : (retail || 0);
        exp = `Deductible: applied ${eventCost.toFixed(2)}.`;
        remainingDeductible -= (typeof deductCost === 'number' ? deductCost : (retail || 0));
        trueOOP += eventCost;
        monthTrueOOPAdd[m] += eventCost;
      } else if (retail != null) {
        const diff = retail - remainingDeductible;
        if (initialPerc) {
          const perc = parseFloat(initialPerc) / 100;
          eventCost = remainingDeductible + perc * diff;
          exp = `Deductible remainder $${remainingDeductible.toFixed(2)} + ${initialPerc} of $${diff.toFixed(2)}.`;
          trueOOP += remainingDeductible + (0.25 * diff); // kept consistent with your interactive logic
          monthTrueOOPAdd[m] += remainingDeductible + (0.25 * diff);
          remainingDeductible = 0;
        } else if (initialCostNum != null) {
          eventCost = remainingDeductible + initialCostNum;
          exp = `Remainder deductible + initial coverage co-pay.`;
          const candidateOOP = remainingDeductible + (0.25 * diff);
          trueOOP += Math.max(candidateOOP, eventCost);
          monthTrueOOPAdd[m] += Math.max(candidateOOP, eventCost);
          remainingDeductible = 0;
        } else {
          eventCost = retail;
          exp = `No level 1 data; full retail.`;
          trueOOP += retail;
          monthTrueOOPAdd[m] += retail;
          remainingDeductible = 0;
        }
      }
    } else if (trueOOP < catastrophicLimit) {
      if (initialPerc && retail != null) {
        const potential = 0.25 * retail;
        eventCost = initialCostNum != null ? initialCostNum : (retail * parseFloat(initialPerc)/100);
        const remainingCap = catastrophicLimit - trueOOP;
        if (potential > remainingCap) {
          eventCost = Math.min(remainingCap, eventCost);
          trueOOP += remainingCap;
          monthTrueOOPAdd[m] += remainingCap;
          exp = `Initial stage; cap almost reached.`;
        } else {
          const added = Math.max(eventCost, potential);
            trueOOP += added;
            monthTrueOOPAdd[m] += added;
            exp = `Initial stage; added ${added.toFixed(2)} to True OOP.`;
        }
      } else if (initialCostNum != null) {
        eventCost = initialCostNum;
        const potential = retail != null ? 0.25 * retail : initialCostNum;
        const add = Math.max(eventCost, potential);
        trueOOP += add;
        monthTrueOOPAdd[m] += add;
        exp = `Initial stage co-pay or 25% choose max = ${add.toFixed(2)}.`;
      } else {
        eventCost = retail || 0;
        trueOOP += eventCost;
        monthTrueOOPAdd[m] += eventCost;
        exp = `Fallback retail.`;
      }
    } else {
      eventCost = 0;
      exp = `Catastrophic stage.`;
    }
    monthTotals[m] += eventCost;
    explanations[m].push(exp);
  });

  const summaryRows = monthNames.map((month, i) => ({
    key: month,
    month,
    drugs: refillMonthSet.has(i) ? breakdown.drugName : '',
    cost: monthTotals[i],
    monthlyOOP: monthTrueOOPAdd[i],
    trueOOP: monthTrueOOPAdd.slice(0, i+1).reduce((a,b)=>a+b,0),
    explanation: explanations[i].join(' ')
  }));

  const totalAnnual = monthTotals.reduce((a,b)=>a+b,0);
  const reachedIdx = summaryRows.findIndex(r => r.trueOOP >= catastrophicLimit);
  let capMessage;
  if (reachedIdx !== -1) {
    capMessage = `Patient will reach the $2000 cap in ${summaryRows[reachedIdx].month}, which is month ${(reachedIdx - startMonth + 1)} from start; total out-of-pocket = $${totalAnnual.toFixed(2)}.`;
  } else {
    capMessage = `Patient will NOT reach the $2000 cap; annual out-of-pocket = $${totalAnnual.toFixed(2)}.`;
  }

  summaryRows.push({
    key:'Annual',
    month:'Annual Total',
    drugs: '',
    cost: totalAnnual,
    monthlyOOP: summaryRows.reduce((s,r)=>s+r.monthlyOOP,0),
    trueOOP: summaryRows[summaryRows.length-1].trueOOP,
    explanation:''
  });

  return { summaryRows, capMessage };
}
/* ---------- PRINT COMPONENT ---------- */

export default function MonthlyBreakdownPrint({
  record,
  selectedPlanDetails,
  drugCosts,
  selectedDaysSupply = '30days',
  selectedPharmacyType = 'retail',
  startDate = new Date(),
  frequency = '30days',
  includeM3P = false,
  paymentPlanStartMonth = startDate.getMonth(),
}) {

  const deductible = selectedPlanDetails?.deductible || 0;

  const calc = useMemo(() => computeDrugCostPrint(record, {
    selectedDaysSupply,
    selectedPharmacyType,
    deductible,
    drugCosts,
    startDate,
    frequency
  }), [record, selectedDaysSupply, selectedPharmacyType, deductible, drugCosts, startDate, frequency]);

  const { summaryRows, capMessage } = useMemo(() => {
    if (!calc) return { summaryRows: [], capMessage: '' };
    return simulateYearSingleDrug({
      breakdown: calc,
      deductible,
      startDate
    });
  }, [calc, deductible, startDate]);

  // ---- derive data for chart/table ----
  const rowsNoAnnual = useMemo(
    () => summaryRows.filter(r => r.month !== 'Annual Total'),
    [summaryRows]
  );

  const m3pRows = useMemo(() => {
    if (!includeM3P) return rowsNoAnnual;
    return applyM3P({
      rows: rowsNoAnnual,
      startMonth: paymentPlanStartMonth,
      annualCap: 2000,
    });
  }, [rowsNoAnnual, includeM3P, paymentPlanStartMonth]);

  // stitch Annual row back on (and totals for M3P)
  const rowsForTable = useMemo(() => {
    const annual = summaryRows.find(r => r.month === 'Annual Total');
    if (!annual) return m3pRows;

    if (!includeM3P) return [...rowsNoAnnual, annual];

    const totalM3PBill = m3pRows.reduce((s, r) => s + (r.m3pBill || 0), 0);
    const lastBal = m3pRows[m3pRows.length - 1]?.balanceInM3P ?? 0;

    return [
      ...m3pRows,
      {
        ...annual,
        m3pMonthlyCap: null,
        m3pBill: totalM3PBill,
        balanceInM3P: lastBal,
        m3pExplanation: null,
      },
    ];
  }, [summaryRows, rowsNoAnnual, m3pRows, includeM3P]);

  const printChartData = useMemo(() => 
    rowsForTable
      .filter(r => r.month !== 'Annual Total')
      .map(r => ({
        month: r.month,
        outOfPocket: r.cost,
        ...(includeM3P ? { m3pBill: r.m3pBill } : {}),
      })),
    [rowsForTable, includeM3P]
  );


  if (!record) {
    return <div className="print-monthly-breakdown"><em>No data.</em></div>;
  }

  return (
    <div className="print-monthly-breakdown">
      <h3>Drug Cost Breakdown</h3>
      <table className="mbp-table" style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
        <thead>
          <tr>
            <th style={th}>Drug Name</th>
            <th style={th}>Frequency</th>
            <th style={th}>Start Date</th>
            <th style={th}>Tier Level</th>
            <th style={th}>Deductible Cost</th>
            <th style={th}>Initial Coverage Cost</th>
            <th style={th}>Cost Share Type</th>
            <th style={th}>Retail Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={td}>{calc.drugName}</td>
            <td style={td}>{calc.frequency}</td>
            <td style={td}>{startDate.toLocaleDateString()}</td>
            <td style={td}>{calc.tier_level_value ?? ''}</td>
            <td style={td}>
              {renderStageValue(calc.costBreakdown, 0)}
            </td>
            <td style={td}>
              {renderStageValue(calc.costBreakdown, 1)}
            </td>
            <td style={td}>
              {(() => {
                const init = calc.costBreakdown.find(c=>c.coverage_level===1);
                if (!init) return 'N/A';
                return init.percentageDisplay ? `Cost share: ${init.percentageDisplay}` : 'Co-pay';
              })()}
            </td>
            <td style={td}>{typeof calc.retailCost === 'number' ? `$${calc.retailCost.toFixed(2)}` : 'N/A'}</td>
          </tr>
        </tbody>
      </table>

      <h3 style={{ marginTop:24 }}>Monthly Breakdown (Jan - Dec {startDate.getFullYear()})</h3>
      <p style={{ margin:'4px 0 12px'}}><em>*All costs are estimates for reference.</em></p>
      <div style={{
        background:'#f5f5f5',
        padding:'8px 10px',
        fontWeight:600,
        marginBottom:12,
        fontSize:12
      }}>{capMessage}</div>

      <table className="mbp-table" style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
        <thead>
          <tr>
            <th style={th}>Month</th>
            <th style={th}>Drugs Refilled</th>
            <th style={{...th, background:'#f5f5f5'}}>Out-of-Pocket (w/o M3P)</th>
            {includeM3P && <th style={{...th, background:'#eef7ff'}}>M3P Bill</th>}
            <th style={th}>Monthly True OOP</th>
            <th style={th}>True OOP Sum</th>
          </tr>
        </thead>
        <tbody>
          {rowsForTable.map((r) => (
            <tr key={r.key}>
              <td style={td}>{r.month}</td>
              <td style={td}>{r.drugs}</td>
              <td style={{...td, background:'#f5f5f5'}}>{formatMoney(r.cost)}</td>
                {includeM3P && (
                    <td style={{...td, background:'#eef7ff'}}>
                    {formatMoney(r.m3pBill)}
                    </td>
                )}
              <td style={td}>{formatMoney(r.monthlyOOP)}</td>
              <td style={td}>{formatMoney(r.trueOOP)}</td>
            </tr>
          ))}
          {!summaryRows.length && (
            <tr><td style={td} colSpan={5} align="center">No data</td></tr>
          )}
        </tbody>
      </table>

      {/* ——— Chart for print ——— */}
      <div id="m3p-section-chart" style={{ marginTop:24, textAlign:'center' }}>
        <h3 style={{ marginBottom:12 }}>Monthly Payment Trend</h3>
            <PaymentPlanChart
            data={printChartData}
            paymentPlanStartMonth={paymentPlanStartMonth}
            showM3P={includeM3P}
            // pass in a smaller size that fits an 8½×11” page
            />
      </div>
    </div>
  );
}

/* ---------- small local helpers for rendering ---------- */

function renderStageValue(costBreakdown, level) {
  const stage = costBreakdown.find(c => c.coverage_level === level);
  if (!stage) return 'N/A';
  return stage.costNumber != null
    ? `$${stage.costNumber.toFixed(2)}`
    : stage.costDisplay || 'N/A';
}

function formatMoney(v) {
  return (typeof v === 'number' && !isNaN(v)) ? `$${v.toFixed(2)}` : 'N/A';
}

// Reusable cell styles:
const th = {
  border:'1px solid #d4dce3',
  padding:'6px 6px',
  textAlign:'left',
  background:'#f1f5f9',
  fontWeight:600
};
const td = {
  border:'1px solid #d4dce3',
  padding:'5px 6px',
  verticalAlign:'top'
};
